import { BackgroundCheckColumns } from '@/backend/features/background-check/entities/background-check';
import { BackgroundCheckStatus } from '@/backend/features/background-check/types';
import { accountEntityName } from '@juvare/contact-management/backend/features/accounts/shared';
import { Filter } from '@juvare/platform-shared/features/storage';
import { differenceInMilliseconds, subMilliseconds } from 'date-fns';

const completeClearStatus: BackgroundCheckStatus = 'completeClear';

export interface BackgroundCheckPeriods {
    backgroundCheckExpiringPeriodDays: number;
    backgroundCheckExpiredPeriodDays: number;
}

export interface BackgroundCheckExpirationState {
    isExpired: boolean;
    isExpiring: boolean;
}

export function getBackgroundCheckExpirationState<Columns extends BackgroundCheckColumns>(
    periods: BackgroundCheckPeriods,
    columns: Columns,
): BackgroundCheckExpirationState {
    if (columns.status !== completeClearStatus || !columns.updatedAt) {
        return { isExpired: false, isExpiring: false };
    }

    const backgroundCheckedAt = new Date(columns.updatedAt);
    const { updateDateWhenExpired, updateDateWhenExpiring } = getBackgroundCheckExpirationDates(periods);
    return {
        isExpired: !!updateDateWhenExpired && backgroundCheckedAt <= updateDateWhenExpired,
        isExpiring: !!updateDateWhenExpiring && backgroundCheckedAt <= updateDateWhenExpiring,
    };
}

export function getBackgroundCheckExpirationDates(periods: BackgroundCheckPeriods): {
    updateDateWhenExpired: Date | undefined;
    updateDateWhenExpiring: Date | undefined;
} {
    if (
        !periods.backgroundCheckExpiredPeriodDays ||
        periods.backgroundCheckExpiredPeriodDays <= 0 ||
        periods.backgroundCheckExpiringPeriodDays > periods.backgroundCheckExpiredPeriodDays
    ) {
        return { updateDateWhenExpiring: undefined, updateDateWhenExpired: undefined };
    }

    const updateDateWhenExpired = new Date();
    updateDateWhenExpired.setDate(updateDateWhenExpired.getDate() - periods.backgroundCheckExpiredPeriodDays);

    let updateDateWhenExpiring = undefined;
    if (periods.backgroundCheckExpiringPeriodDays && periods.backgroundCheckExpiringPeriodDays >= 0) {
        updateDateWhenExpiring = new Date(updateDateWhenExpired);
        updateDateWhenExpiring.setDate(updateDateWhenExpiring.getDate() + periods.backgroundCheckExpiringPeriodDays);
    }

    return { updateDateWhenExpired, updateDateWhenExpiring };
}

export function getBackgroundCheckShowOnlyExpiringFilters(periods: BackgroundCheckPeriods): Filter<BackgroundCheckColumns>[] {
    const { updateDateWhenExpiring } = getBackgroundCheckExpirationDates(periods);
    if (!updateDateWhenExpiring) {
        return [];
    }

    return [
        {
            column: 'updatedAt',
            op: '<=',
            value: updateDateWhenExpiring.getTime(),
        },
        {
            column: 'status',
            op: '=',
            value: completeClearStatus,
        },
    ];
}

export function getBackgroundCheckExpirationStorageFilters(
    periods: BackgroundCheckPeriods,
    options?: {
        treatExpiredAsExpiring?: boolean;
        previousCheckDateToExcludeResults?: Date | undefined;
    },
): {
    expiringBackgroundCheckFilters: Filter<BackgroundCheckColumns>[] | undefined;
    expiredBackgroundCheckFilters: Filter<BackgroundCheckColumns>[] | undefined;
} {
    const { updateDateWhenExpired, updateDateWhenExpiring } = getBackgroundCheckExpirationDates(periods);
    if (!updateDateWhenExpiring || !updateDateWhenExpired) {
        return {
            expiringBackgroundCheckFilters: undefined,
            expiredBackgroundCheckFilters: undefined,
        };
    }

    const commonFilters: Filter<BackgroundCheckColumns>[] = [
        {
            column: 'status',
            op: '=',
            value: completeClearStatus,
        },
        {
            column: 'accountId',
            op: 'in',
            subFilter: { entity: accountEntityName, filter: [{ column: 'systemStatus', op: '=', value: 'active' }] },
        },
    ];

    const expiringBackgroundCheckFilters: Filter<BackgroundCheckColumns>[] = [
        ...commonFilters,
        { column: 'updatedAt', op: '<=', value: updateDateWhenExpiring.getTime() },
    ];
    const expiredBackgroundCheckFilters: Filter<BackgroundCheckColumns>[] = [
        ...commonFilters,
        { column: 'updatedAt', op: '<=', value: updateDateWhenExpired.getTime() },
    ];

    if (!options?.treatExpiredAsExpiring) {
        expiringBackgroundCheckFilters.push({
            column: 'updatedAt',
            op: '>',
            value: updateDateWhenExpired.getTime(),
        });
    }

    if (options?.previousCheckDateToExcludeResults) {
        const msSinceLastCheck = differenceInMilliseconds(new Date(), options.previousCheckDateToExcludeResults);

        expiringBackgroundCheckFilters.push({
            column: 'updatedAt',
            op: '>=',
            value: subMilliseconds(updateDateWhenExpiring, msSinceLastCheck).getTime(),
        });
        expiredBackgroundCheckFilters.push({
            column: 'updatedAt',
            op: '>=',
            value: subMilliseconds(updateDateWhenExpired, msSinceLastCheck).getTime(),
        });
    }

    return {
        expiringBackgroundCheckFilters,
        expiredBackgroundCheckFilters,
    };
}
